/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Andrey Dubov
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.14.0
 */

import { Fragment, useState } from 'react';

import type { FormikHelpers, FormikProps } from 'formik';
import { Formik } from 'formik';
import last from 'lodash/last';
import { useDispatch, useSelector } from 'react-redux';

import { InfoBlock, LinkBlock } from '@components/common';
import {
  FormBlockStyled,
  FormControlStyled,
} from '@components/common/FormStyled/FormStyled';
import { LabelMarkStyled } from '@components/common/Input/styled/InputStyled';
import { EColorStatus } from '@modules/app/appConstants';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import { Loader } from '@modules/loader/components/Loader';
import { useModalOpenError } from '@modules/modal/hooks/useModalOpenError';
import { useModalOpenSuccess } from '@modules/modal/hooks/useModalOpenSuccess';
import { modalOpen } from '@modules/modal/modalActions';
import { EModalStatus } from '@modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '@modules/role/components/ButtonServiceUserDenied';

import { useSubscriberIdContext } from '../../../../context';
import {
  mapSubscriberIdPersonalDataFieldConflicts,
  PERSONAL_ACCOUNT_CLOSE_CODE,
  PersonalDataEditValidationSchema,
} from '../../const/SubscriberIdPersonalDataEdit';
import { personalDataTranslationSelector } from '../../selectors';
import {
  useGetPersonalDataFileQuery,
  useGetPersonalDataInfoQuery,
  useGetPersonalDataMetaQuery,
  useSavePersonalDataMutation,
  useUploadPersonalDataFileMutation,
} from '../../store';
import type { IPersonalDataInfoEdit } from '../../types/info-edit';
import { PersonalDataType } from '../../types/meta';
import {
  SubscriberInfoDataInfoEditFormStyled,
  SubscriberInfoDataInfoFieldFileStyled,
} from '../styled/SubscriberInfoDataInfoStyled';
import PersonalDataEditAddress from './PersonalDataEditAddress';
import PersonalDataEditCommon from './PersonalDataEditCommon';
import PersonalDataEditConfirmModal from './PersonalDataEditConfirmModal';
import PersonalDataEditDocument from './PersonalDataEditDocument';
import { PersonalDataEditFile } from './PersonalDataEditFile';
import PersonalDataEditNationality from './PersonalDataEditNationality';

export interface ISubscriberIdPersonalDataEdit {
  onCancel: () => void;
  onSuccess: () => void;
}

const PersonalDataEdit = ({
  onCancel,
  onSuccess,
}: ISubscriberIdPersonalDataEdit) => {
  const { subscriberId } = useSubscriberIdContext();

  const dispatch = useDispatch();

  const { open: openModalSuccess } = useModalOpenSuccess();
  const { open: openModalError } = useModalOpenError();

  const tabTranslation = useSelector(personalDataTranslationSelector());
  const translationCommon = useSelector(makeSelectTranslationCommon());

  const [excludeFiles, setExcludeFiles] = useState<string[]>([]);

  const { data: meta } = useGetPersonalDataMetaQuery(subscriberId);

  const { data: info } = useGetPersonalDataInfoQuery({
    subscriberId,
    defaultDoc: meta?.docTypes[0],
  });

  const { data: file } = useGetPersonalDataFileQuery(subscriberId);

  const [, { isLoading: isFileUploadLoading }] =
    useUploadPersonalDataFileMutation();

  /*
   * -------------------------------------------
   *           INITIALIZE FORM
   * -------------------------------------------
   */

  const [docTypeSelected, setDocTypeSelected] = useState(
    meta?.docTypes[info?.document.type.value ?? ''],
  );
  /*
   * -------------------------------------------
   *           EVENT HANDLERS
   * -------------------------------------------
   */

  const [save, { isLoading: isSaving }] = useSavePersonalDataMutation();

  const openConfirmModal = (
    personalData: IPersonalDataInfoEdit,
    message: string,
  ) => {
    dispatch(
      modalOpen({
        render: (p) => (
          <PersonalDataEditConfirmModal
            excludeFiles={excludeFiles}
            message={message}
            personalData={personalData}
            subscriberId={subscriberId}
            onCancel={() => {
              onCancel();
              p.onCloseModal();
            }}
            onCloseModal={p.onCloseModal}
            onConfirm={p.onCloseModal}
            onSuccess={onSuccess}
          />
        ),
      }),
    );
  };

  const onSubmit = (
    personalData: IPersonalDataInfoEdit,
    { setFieldError, setSubmitting }: FormikHelpers<IPersonalDataInfoEdit>,
  ) => {
    save({ subscriberId, personalData, excludeFiles })
      .unwrap()
      .then(({ conflicts }) => {
        if (conflicts?.length) {
          /* Если пришел конфликт о подтверждении данных и он только один, тогда показать модальное окно*/
          const isNeedToConfirm = conflicts.find(
            (conflict) => conflict.code === PERSONAL_ACCOUNT_CLOSE_CODE,
          );
          if (isNeedToConfirm && conflicts.length === 1) {
            openConfirmModal(personalData, isNeedToConfirm.message);
          } else {
            const fieldNames = [
              ...Object.values(PersonalDataType),
              'registrationAddress',
            ];
            // Если ошибка связана с заполнением полей
            let isFieldsHasConflicts = false;

            const mesages = conflicts.reduce((errorMesages, conflict) => {
              const name = last(conflict.path.split('.'));

              if (name && errorMesages?.[name]) {
                errorMesages[name] = errorMesages[name].concat(
                  `\n${conflict.message}`,
                );
              }
              if (name && !errorMesages?.[name]) {
                errorMesages[name] = conflict.message;
              }
              return errorMesages;
            }, {} as Record<string, string>);

            fieldNames.forEach((name) => {
              if (
                mesages[name] &&
                mapSubscriberIdPersonalDataFieldConflicts[
                  name as keyof typeof mapSubscriberIdPersonalDataFieldConflicts
                ]
              ) {
                setFieldError(
                  mapSubscriberIdPersonalDataFieldConflicts[
                    name as keyof typeof mapSubscriberIdPersonalDataFieldConflicts
                  ],
                  mesages[name],
                );
                isFieldsHasConflicts = true;
              }
            });

            // Если конфликты не связаны с конкретным полем
            if (!isFieldsHasConflicts) {
              openModalError(conflicts[0].message);
            }
          }
        } else {
          openModalSuccess(
            tabTranslation.personalDataEdit.modal[EModalStatus.success]
              .saveData,
            onSuccess,
          );
        }
      })
      .catch(() => {
        openModalError(
          tabTranslation.personalDataEdit.modal[EModalStatus.failure].saveData,
        );
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (!meta || !info) return null;

  const { personalDataEdit: tPersonalDataEdit } = tabTranslation;

  const [surname, name, patronymic] =
    info.personalData.fullName?.split(' ') ?? [];
  const initialValue: IPersonalDataInfoEdit = {
    address: {
      airId: info.address?.airId ?? '',
      addressId: info.address?.addressId ?? '',
      addressString: info.address?.addressString ?? '',
    },
    personalData: {
      ...info.personalData,
      name,
      patronymic,
      patronymicIsOptional: false,
      surname,
      nationalityInfo: {
        def: info.personalData.nationalityInfo?.addressString || '',
        id: info.personalData.nationalityInfo?.addressId,
      },
    },
    document: info.document,
    files: file || [],
  };

  return (
    <Loader showLoader={isSaving}>
      <SubscriberInfoDataInfoEditFormStyled>
        <Formik
          initialValues={initialValue}
          validationSchema={PersonalDataEditValidationSchema(
            translationCommon.form.validation,
            meta.docTypes,
            meta.personalDataTypes,
            meta.passportId,
          )}
          onSubmit={onSubmit}
        >
          {({
            dirty,
            isSubmitting,
            isValid,
            resetForm,
            submitForm,
          }: FormikProps<IPersonalDataInfoEdit>) => (
            <Fragment>
              <div className="edit-form__personal-data">
                <PersonalDataEditCommon />
              </div>
              <PersonalDataEditNationality docTypeSelected={docTypeSelected} />
              <div className="edit-form__address">
                <PersonalDataEditAddress initialValue={initialValue.address} />
              </div>
              <div className="edit-form__document">
                <PersonalDataEditDocument
                  docTypeSelected={docTypeSelected}
                  setDocTypeSelected={setDocTypeSelected}
                />
                <SubscriberInfoDataInfoFieldFileStyled>
                  <PersonalDataEditFile
                    docTypeSelected={docTypeSelected}
                    excludeFiles={excludeFiles}
                    setExcludeFiles={setExcludeFiles}
                  />
                </SubscriberInfoDataInfoFieldFileStyled>
              </div>
              <div style={{ marginBottom: 24 }}>
                <LabelMarkStyled>*</LabelMarkStyled>&nbsp;
                {tPersonalDataEdit.requiredMessage}
              </div>
              <FormBlockStyled>
                <InfoBlock colorStatus={EColorStatus.WARNING}>
                  {tPersonalDataEdit.warning.personalDataConfirmation}
                </InfoBlock>
              </FormBlockStyled>
              <FormControlStyled>
                <ButtonServiceUserDenied
                  disabled={
                    !isValid || isSubmitting || !dirty || isFileUploadLoading
                  }
                  kind="medium"
                  type="submit"
                  onClick={submitForm}
                >
                  {translationCommon.form.save}
                </ButtonServiceUserDenied>
                <LinkBlock
                  onClick={() => {
                    resetForm();
                    onCancel();
                  }}
                >
                  {translationCommon.form.cancel}
                </LinkBlock>
              </FormControlStyled>
            </Fragment>
          )}
        </Formik>
      </SubscriberInfoDataInfoEditFormStyled>
    </Loader>
  );
};

export default PersonalDataEdit;
